/**
 * Apercu en direct de la carte de prise.
 *
 * Le gabarit vient de cartes-gabarits.js, genere depuis cartes/gabarits/*.html :
 * ce que le client voit ici est exactement ce que l'atelier lui livrera.
 *
 * Quand l'offre attend plusieurs photos, des pastilles numerotees permettent
 * de passer d'une carte a l'autre.
 */
// Les quatre univers d'origine ne sont plus vendus. On les reconnait quand
// meme : une fiche produit pas encore migree continue de s'afficher.
const UNIVERS_HISTORIQUES = {
  'Carnet': 'carnet',
  'Trophée': 'trophee',
  'Carte marine': 'marine',
  'Première prise': 'premiere',
};

const CHAMPS = {
  'Espèce': 'espece',
  'Poids (kg)': 'poids',
  'Taille (cm)': 'taille',
  'Lieu': 'lieu',
  'Date de la prise': 'date',
  'Mouche ou leurre': 'leurre',
  'Signature': 'pecheur',
  'Devise': 'devise',
  'Mention du recto': 'mention_recto',
  'Pays du timbre': 'pays_timbre',
  'Message au verso': 'message',
  'Relâchée (no-kill)': 'nokill',
};

// Ces champs ne servent qu'à la carte postale : ailleurs ils sont masqués ET
// désactivés — un champ seulement masqué part quand même dans la commande.
const CHAMPS_CARTE_POSTALE = ['Devise', 'Mention du recto', 'Pays du timbre', 'Message au verso'];

// Le timbre porte le pays de la prise. Table jumelle de la fonction timbre()
// de scripts/render_prise.mjs — les deux doivent dire la meme chose.
function timbre(pays) {
  const nom = String(pays ?? '').trim().toUpperCase();
  const cle = nom.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const n = nom.length;
  const lys = cle.includes('quebec');
  const erable = !lys && cle.includes('canada');
  let taille = 6, espacement = 0.6;
  for (const [t, e] of [[13, 2.6], [12, 2.2], [11, 1.8], [10, 1.4], [9, 1.2], [8, 1], [7, 0.8], [6, 0.6]]) {
    if (n * (t * 0.62 + e) <= 96) { taille = t; espacement = e; break; }
  }
  return {
    pays_timbre: nom,
    taille_pays: taille,
    espacement_pays: espacement,
    embleme_lys: lys,
    embleme_erable: erable,
    embleme_poisson: !lys && !erable,
  };
}

// Meme table que scripts/render_prise.mjs : la fenetre photo suit le cadrage.
const CADRES = {
  Portrait: { ratio: '3 / 4', hauteur: '56%' },
  'Carré':  { ratio: '1 / 1', hauteur: '48%' },
  Paysage:  { ratio: '4 / 3', hauteur: '38%' },
};

// Une carte vide ne montre pas le produit, et c'est le produit qu'on vend.
const EXEMPLE = {
  espece: 'Votre espèce', poids: '—', taille: '—',
  lieu: 'Votre rivière', date: 'Le jour de la prise', pecheur: '',
  // Uniquement pour l'aperçu : le client voit à quoi ressemblent ces textes
  // avant d'écrire les siens. Ils ne partent jamais dans la commande.
  devise: 'Passion\nNature\nLiberté', mention_recto: "Au fil de l'eau",
  pays_timbre: 'Québec',
};

const attendre = (fn, ms = 140) => {
  let t;
  return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
};

class ApercuCarte extends HTMLElement {
  connectedCallback() {
    if (this.dataset.pret) return;
    this.dataset.pret = '1';

    if (!window.CartesGabarits) {
      window.addEventListener('cartes-gabarits:pret', () => this.demarrer(), { once: true });
      return;
    }
    this.demarrer();
  }

  demarrer() {
    this.cadre = this.querySelector('[ref="cadre"]');
    this.filigrane = this.querySelector('[ref="filigrane"]');
    this.onglets = this.querySelector('[ref="onglets"]');
    this.faces = this.querySelector('[ref="faces"]');
    this.scene = this.querySelector('.__scene');
    this.actif = 1;
    this.face = null; // aucune face choisie a la main : on montre la premiere

    this.peindre = attendre(() => this.composer());

    // On ecoute le document : la fiche produit est un seul formulaire, et le
    // theme re-rend ses sections sans prevenir.
    for (const e of ['input', 'change']) {
      document.addEventListener(e, (ev) => this.surChangement(ev), true);
    }
    // Une photo ajoutee bascule l'apercu sur SA carte.
    document.addEventListener('photos:maj', (ev) => {
      this.photos = ev.detail.photos;
      this.majOnglets();
      this.peindre();
    });

    this.composer();
  }

  surChangement(e) {
    const n = e.target?.name ?? '';
    const optionEnvoi = window.CartesGabarits?.ENVOI?.option ?? 'Envoi';
    if (n.startsWith('properties[') || n.startsWith('Livrable') ||
        n.startsWith('Univers') || n.startsWith(optionEnvoi)) {
      if (e.target.type === 'file') return; // gere par l'evenement photos:maj
      this.peindre();
    }
  }

  nbPhotos() {
    return document.querySelectorAll('product-photo-component .__groupe').length || 1;
  }

  majOnglets() {
    if (!this.onglets) return;
    const n = this.nbPhotos();
    if (n < 2) { this.onglets.hidden = true; return; }

    // Basculer sur la derniere photo deposee : c'est celle qu'on veut voir.
    const derniere = (this.photos ?? []).reduce((acc, u, i) => (u ? i + 1 : acc), 0);
    if (derniere) this.actif = derniere;

    this.onglets.hidden = false;
    this.onglets.replaceChildren(
      ...Array.from({ length: n }, (_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = '__onglet';
        b.textContent = String(i + 1);
        b.setAttribute('aria-pressed', String(this.actif === i + 1));
        if (!(this.photos ?? [])[i]) b.classList.add('is-vide');
        b.addEventListener('click', () => { this.actif = i + 1; this.majOnglets(); this.composer(); });
        return b;
      })
    );
  }

  // Suffixe des proprietes : « Espèce » quand il n'y a qu'une carte,
  // « Espèce 2 » pour la deuxieme d'un lot.
  valeur(cle) {
    const n = this.nbPhotos();
    const noms = n > 1 ? [`${cle} ${this.actif}`, cle] : [cle];
    for (const nom of noms) {
      const el = document.querySelector(`[name="properties[${CSS.escape(nom)}]"]`);
      if (el) return el;
    }
    return null;
  }

  donnees() {
    const n = this.nbPhotos();
    const d = { ...EXEMPLE };

    for (const [prop, cle] of Object.entries(CHAMPS)) {
      const el = this.valeur(prop);
      if (!el) continue;
      if (el.type === 'checkbox') { if (el.checked) d[cle] = true; continue; }
      const v = el.value.trim();
      if (v) d[cle] = v;
    }
    if (d.poids && d.poids !== '—' && !/[a-z]/i.test(d.poids)) d.poids += ' kg';
    if (d.taille && d.taille !== '—' && !/[a-z]/i.test(d.taille)) d.taille += ' cm';

    // Le cadrage est un choix du client, plus une deduction.
    const nomCadrage = n > 1 ? `Cadrage ${this.actif}` : 'Cadrage';
    const coche = document.querySelector(`input[name="properties[${CSS.escape(nomCadrage)}]"]:checked`);
    const cadre = CADRES[coche?.value] ?? CADRES.Portrait;
    d.ratio = cadre.ratio;
    d.hauteur_photo = cadre.hauteur;

    // La carte postale prend l'orientation du cadrage : une photo verticale
    // sur une carte en paysage, c'est le poisson coupe.
    d.orientation_carte = (coche?.value ?? 'Portrait') === 'Portrait' ? 'portrait' : 'paysage';
    d.face = this.faceActive();

    // Le verso appartient au mot du client : ses releves tiennent sur une ligne.
    d.releve = [d.espece, d.poids, d.taille, d.lieu, d.date, d.leurre, d.nokill ? 'relâchée' : null]
      .filter((v) => v && v !== '—')
      .join(' · ');

    Object.assign(d, timbre(d.pays_timbre));

    const photo = (this.photos ?? [])[this.actif - 1];
    if (photo) d.photo = photo;
    return d;
  }

  // La variante peut etre rendue en boutons radio ou en menu deroulant selon
  // le reglage du theme : on accepte les deux.
  // Les deux themes n'ecrivent pas le nom d'une option de la meme facon, et
  // Dawn lui-meme en change selon le reglage du selecteur :
  //
  //   Dawn en boutons      <input name="Livrable-1">        (le theme publie)
  //   Dawn en deroulant    <select name="options[Livrable]">
  //   Horizon              <select name="options[Livrable]">
  //
  // On accepte les deux ecritures. N'en reconnaitre qu'une, c'est un apercu
  // qui reste muet sur le choix du client — sans erreur, sans rien dans la
  // console, et sans qu'on sache pourquoi.
  valeurOption(nom) {
    const selecteurs = [
      `input[type="radio"][name^="${nom}"]:checked`,
      `input[type="radio"][name="options[${nom}]"]:checked`,
      `select[name^="${nom}"]`,
      `select[name="options[${nom}]"]`,
    ];
    for (const s of selecteurs) {
      const v = document.querySelector(s)?.value?.trim();
      if (v) return v;
    }
    return null;
  }

  livrableChoisi() {
    const table = window.CartesGabarits?.LIVRABLES ?? {};
    const valeur = this.valeurOption('Livrable') ?? this.valeurOption('Univers');
    if (!valeur) return Object.keys(table)[0] ?? 'carnet';
    const parNom = Object.keys(table).find((c) => table[c].nom === valeur);
    return parNom ?? (table[valeur] ? valeur : UNIVERS_HISTORIQUES[valeur] ?? 'carnet');
  }

  // Le client a-t-il demande que sa carte soit postee ? L'option « Envoi »
  // porte ce choix ; son libelle exact vient de scripts/lib/livrables.mjs par
  // cartes-gabarits.js, pour que les deux ne puissent pas diverger.
  envoiPostalChoisi() {
    const envoi = window.CartesGabarits?.ENVOI;
    if (!envoi) return false;
    // Plusieurs paliers envoient a la poste — 1, 3 ou 10 cartes — et le verso
    // est le meme pour tous : c'est la meme carte, imprimee plusieurs fois.
    return (envoi.postaux ?? []).includes(this.valeurOption(envoi.option));
  }

  // Les faces d'un livrable : une seule pour le carnet et le marque-page,
  // recto et verso pour la carte postale.
  //
  // Et un VERSO DIFFERENT selon l'envoi. Une carte postee n'a ni le timbre
  // dessine ni le paysage de lac — leur place revient a l'adresse et au
  // code-barres. Montrer le verso souvenir a qui a paye un envoi postal, ce
  // serait lui promettre une carte qu'il ne recevra pas.
  facesDuLivrable() {
    const l = window.CartesGabarits?.LIVRABLES?.[this.livrableChoisi()];
    if (!l) return ['carte'];
    return (this.envoiPostalChoisi() && l.facesPostales) || l.faces;
  }

  faceActive() {
    const faces = this.facesDuLivrable();
    return faces.includes(this.face) ? this.face : faces[0];
  }

  // Un livrable a deux faces : on donne au client de quoi retourner la carte.
  majFaces() {
    if (!this.faces) return;
    const faces = this.facesDuLivrable();
    if (faces.length < 2) { this.faces.hidden = true; return; }

    const active = this.faceActive();
    this.faces.hidden = false;
    this.faces.replaceChildren(
      ...faces.map((f) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = '__onglet';
        b.textContent = window.CartesGabarits?.ETIQUETTES_FACES?.[f]
          ?? f.charAt(0).toUpperCase() + f.slice(1);
        b.setAttribute('aria-pressed', String(active === f));
        b.addEventListener('click', () => { this.face = f; this.majFaces(); this.composer(); });
        return b;
      })
    );
  }

  // La devise, la mention, le pays du timbre et le mot du verso n'ont de sens
  // que sur la carte postale. Ailleurs on masque les champs ET on les desactive :
  // un champ seulement masque part quand meme dans la commande, et l'atelier
  // lirait des consignes qui ne s'impriment nulle part. C'est ce composant qui
  // s'en charge parce que c'est lui qui sait, a chaque instant, quel livrable
  // est choisi.
  majChampsCartePostale(actif) {
    for (const cle of CHAMPS_CARTE_POSTALE) {
      const champ = document.querySelector(`[name="properties[${CSS.escape(cle)}]"]`);
      if (!champ) continue;
      // Deux thèmes, deux enveloppes : « product-custom-property-component »
      // chez Horizon, « .prise-champ » chez Dawn.
      const bloc = champ.closest('.prise-champ')
        ?? champ.closest('product-custom-property-component')
        ?? champ.parentElement;
      if (bloc) bloc.hidden = !actif;
      champ.disabled = !actif;
    }
  }

  composer() {
    const table = window.CartesGabarits?.LIVRABLES ?? {};
    const cle = this.livrableChoisi();
    const gabarit = window.CartesGabarits?.GABARITS?.[table[cle]?.gabarit ?? cle];
    if (!gabarit || !this.cadre) return;

    this.majFaces();
    this.majChampsCartePostale(Boolean(table[cle]?.message));
    const d = this.donnees();

    // Le cadre prend la forme de l'objet vendu : 9/16 pour le carnet, la carte
    // postale a ses proportions, le marque-page les siennes.
    // Sauf le verso postal : sa page est en paysage quel que soit le cadrage,
    // et le cadre doit l'etre aussi, sinon il coupe la zone d'adresse.
    const ratio = (String(this.faceActive()).startsWith('postal') && table[cle]?.ratioPostal)
      || table[cle]?.ratios?.[d.orientation_carte];
    if (this.scene && ratio) this.scene.style.aspectRatio = ratio;

    this.cadre.srcdoc = window.CartesGabarits.rendre(gabarit, d);
    if (this.filigrane) this.filigrane.hidden = true;
  }
}

if (!customElements.get('product-apercu-component')) {
  customElements.define('product-apercu-component', ApercuCarte);
}
